import { Record, Map } from "immutable";
import { AnyAction } from "redux";

import { defaultValues } from "@config/defaultValues";

import {
  CANCELLATION_STATE,
  JobAction,
  JOB_ACTION,
  JOB_FETCH_TYPE,
  UI_STATE_ENUM,
} from "@typings/jobs";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import {
  ACTION_SUCCESS,
  ACTION_NO_DATA,
  ACTION_FAIL,
  ACTION_PENDING,
} from "@services/AsyncActionCreatorFactory";

import {
  FETCH_JOBS2,
  CANCEL_START,
  CANCEL_REASON,
  CANCEL_DONE,
  CANCEL_PAYMENT,
  INIT_AGENT_REPLACEMENT,
  RATE_JOB,
  SET_JOB_RATING,
  SET_KEY_PICK,
  SET_SPECIAL_INSTRUCTIONS,
  SET_CANCELLATION_REASON,
  INITIALIZE_JOB_RESCHEDULING,
  FETCH_JOB_BY_UUID,
  FETCH_RESCHEDULING_AVAILABILITIES,
  INITIALIZE_AUTOMATIC_AGENT_REPLACEMENT,
  FETCH_NEXT_UPCOMING_JOB,
  FETCH_JOBS_BEYOND_PLANNING_WINDOW,
} from "./jobs2.actions";
import { Jobs2Reducer } from "./jobs2.typings";
import {
  agentReplacementFactory,
  CancellationFactory,
  JobsLocationFactory,
  JobsLocationBeyondPlanningWindowFactory,
  MapJobsFactory,
  reschedulingFactory,
  reschedulingTimeslotsFactory,
  SingleJobFactory,
} from "./jobs2.factories";

const initialState = Record<Jobs2Reducer>({
  jobsByLocation: Map(),
  allJobs: Map(),
  loadingState: LOADING_STATE.IDLE,
  holidaysResponsePendingJobs: 0,
  cancellationReason: null,
  nextUpcomingJob: null,
  rescheduling: Record({
    ...defaultValues.rescheduling,
    timeslots: Record(defaultValues.rescheduling.timeslots)(),
  })(),
  agentReplacement: Record(defaultValues.agentReplacement)(),

  // Beyond planning window
  jobsByLocationBeyondPlanningWindow: Map(),
  loadingStateBeyondPlanningWindow: LOADING_STATE.IDLE,
});

const jobs2Reducer = (
  store: Record<Jobs2Reducer> = initialState(),
  action: AnyAction,
): Record<Jobs2Reducer> => {
  const { payload, type } = action;

  switch (type) {
    case `${FETCH_JOBS2}`: {
      return store.set("loadingState", LOADING_STATE.LOADING);
    }

    case `${FETCH_JOBS2}${ACTION_FAIL}`: {
      return store.set("loadingState", LOADING_STATE.FAILED);
    }

    case `${FETCH_JOBS2}${ACTION_SUCCESS}`: {
      const {
        data: { jobs, hasNextPage, cursor, holidaysResponsePendingJobs },
      } = payload;
      const {
        extra: { uiState },
      } = action;

      if (jobs.length === 0) {
        return store.set("loadingState", LOADING_STATE.SUCCEEDED);
      }

      const currentJobsList = store
        .get("jobsByLocation")
        .get(jobs[0].locationUuid);

      const isJobUpcoming = uiState === JOB_FETCH_TYPE.UPCOMING;

      // persist jobs after fetching for different type
      const otherTypeJobs = isJobUpcoming
        ? JOB_FETCH_TYPE.PREVIOUS
        : JOB_FETCH_TYPE.UPCOMING;
      const otherJobs = currentJobsList?.get(otherTypeJobs);

      const hasNextPagePrevious = currentJobsList?.get("hasNextPagePrevious");
      const hasNextPageUpcoming = currentJobsList?.get("hasNextPageUpcoming");

      return store
        .setIn(
          ["jobsByLocation", jobs[0].locationUuid],
          JobsLocationFactory({
            cursor,
            [uiState as JOB_FETCH_TYPE]: MapJobsFactory(
              jobs,
              currentJobsList?.get(uiState),
              uiState === JOB_FETCH_TYPE.UPCOMING,
            ),
            [otherTypeJobs]: otherJobs || Map(),
            hasNextPagePrevious: !isJobUpcoming
              ? hasNextPage
              : hasNextPagePrevious,
            hasNextPageUpcoming: isJobUpcoming
              ? hasNextPage
              : hasNextPageUpcoming,
          }),
        )
        .set(
          "allJobs",
          MapJobsFactory(
            jobs,
            store.get("allJobs"),
            uiState === JOB_FETCH_TYPE.UPCOMING,
          ),
        )
        .set("nextUpcomingJob", null)
        .set("loadingState", LOADING_STATE.SUCCEEDED)
        .set("holidaysResponsePendingJobs", holidaysResponsePendingJobs);
    }

    case `${FETCH_JOBS2}${ACTION_NO_DATA}`: {
      return store.set("loadingState", LOADING_STATE.SUCCEEDED);
    }

    case `${FETCH_JOB_BY_UUID}${ACTION_SUCCESS}`: {
      const { data } = payload;
      const { params } = action as {
        params?: { jobFetchType: JOB_FETCH_TYPE };
      };

      let { uiState } = data;

      if (
        !!params &&
        "jobFetchType" in params &&
        Object.keys(JOB_FETCH_TYPE).includes(params.jobFetchType)
      ) {
        uiState = params.jobFetchType;
      }

      return store
        .setIn(["allJobs", data.uuid], SingleJobFactory(data))
        .setIn(
          ["jobsByLocation", data.locationUuid, uiState, data.uuid],
          SingleJobFactory(data),
        );
    }

    case `${FETCH_NEXT_UPCOMING_JOB}${ACTION_SUCCESS}`: {
      const { data } = payload;

      if (data.jobs.length !== 1) {
        return store.set("nextUpcomingJob", null);
      }

      return store.set("nextUpcomingJob", SingleJobFactory(data.jobs[0]));
    }

    case `${CANCEL_START}${ACTION_SUCCESS}`: {
      const { params } = action;
      const { data } = payload;

      return store
        .setIn(
          ["allJobs", params.id, "cancellationState"],
          CANCELLATION_STATE.AWAITING_REASON_ANSWER,
        )
        .setIn(
          ["allJobs", params.id, "cancellationProcess"],
          CancellationFactory(data),
        );
    }

    case `${CANCEL_START}${ACTION_FAIL}`: {
      const { params } = action;

      return store.setIn(
        ["allJobs", params.id, "cancellationState"],
        CANCELLATION_STATE.FAILED,
      );
    }

    case `${CANCEL_REASON}${ACTION_SUCCESS}`: {
      const { params } = action;

      return store.setIn(
        ["allJobs", params.id, "cancellationState"],
        CANCELLATION_STATE.AWAITING_PAYMENT_SELECTION,
      );
    }

    case `${CANCEL_PAYMENT}${ACTION_FAIL}`: {
      const { params } = action;

      if (params.cancellationV2Enabled) return store;

      return store
        .setIn(
          ["allJobs", params.id, "cancellationState"],
          CANCELLATION_STATE.NOT_STARTED,
        )
        .setIn(["allJobs", params.id, "cancellationProcess"], undefined)
        .setIn(["allJobs", params.id, "uiState"], UI_STATE_ENUM.UPCOMING);
    }

    case CANCEL_DONE: {
      const allJobsPath = ["allJobs", payload.id];
      const jobsByLocationPath = [
        "jobsByLocation",
        payload.jobLocationUuid,
        JOB_FETCH_TYPE.UPCOMING,
        payload.id,
      ];

      return store
        .setIn([...allJobsPath, "cancellationState"], CANCELLATION_STATE.DONE)
        .setIn([...allJobsPath, "amount"], payload.amount)
        .setIn([...allJobsPath, "cancellationProcess"], undefined)
        .setIn([...allJobsPath, "uiState"], UI_STATE_ENUM.CANCELLED)
        .setIn(
          [...allJobsPath, "actions"],
          store
            .getIn([...allJobsPath, "actions"])
            ?.filter(
              (action: JobAction) =>
                action.key === JOB_ACTION.DETAILS ||
                action.key === JOB_ACTION.DOWNLOAD_INVOICE,
            ) || [],
        )
        .setIn(
          [...jobsByLocationPath, "cancellationState"],
          CANCELLATION_STATE.DONE,
        )
        .setIn([...jobsByLocationPath, "amount"], payload.amount)
        .setIn([...jobsByLocationPath, "cancellationProcess"], undefined)
        .setIn([...jobsByLocationPath, "uiState"], UI_STATE_ENUM.CANCELLED)
        .setIn(
          [...jobsByLocationPath, "actions"],
          store
            .getIn([...jobsByLocationPath, "actions"])
            ?.filter(
              (action: JobAction) =>
                action.key === JOB_ACTION.DETAILS ||
                action.key === JOB_ACTION.DOWNLOAD_INVOICE,
            ),
        );
    }

    case `${INIT_AGENT_REPLACEMENT}${ACTION_SUCCESS}`: {
      const { params } = action;
      return store.setIn(
        ["allJobs", params.id, "uiState"],
        UI_STATE_ENUM.REPLACEMENT_PENDING,
      );
    }

    case `${RATE_JOB}${ACTION_SUCCESS}`:
      return store
        .setIn(
          ["allJobs", action.params.id, "ratingComment"],
          action.params.comment,
        )
        .setIn(["AllJobs", action.params.id, "rating"], action.params.rating)
        .setIn(
          [
            "jobsByLocation",
            action.params.locationUuid,
            JOB_FETCH_TYPE.PREVIOUS,
            action.params.id,

            "ratingComment",
          ],
          action.params.comment,
        )
        .setIn(
          [
            "jobsByLocation",
            action.params.locationUuid,
            JOB_FETCH_TYPE.PREVIOUS,
            action.params.id,
            "rating",
          ],
          action.params.rating,
        );

    case SET_JOB_RATING: {
      const locationUuid = store
        .get("allJobs")
        .get(payload.jobUuid)
        ?.get("locationUuid");

      return store
        .setIn(["allJobs", payload.jobUuid, "rating"], payload.rating)
        .setIn(
          [
            "jobsByLocation",
            locationUuid,
            JOB_FETCH_TYPE.PREVIOUS,
            payload.jobUuid,
            "rating",
          ],
          payload.rating,
        );
    }

    case `${SET_KEY_PICK}${ACTION_SUCCESS}`: {
      const { params } = action;

      const jobByLocationPath = [
        "jobsByLocation",
        params.locationUuid,
        JOB_FETCH_TYPE.UPCOMING,
        params.id,
      ];

      return store
        .setIn(["allJobs", params.id, "keyPick"], params.keyPick)
        .setIn(
          ["allJobs", params.id, "locationComment"],
          params.locationComment,
        )
        .setIn([...jobByLocationPath, "keyPick"], params.keyPick)
        .setIn(
          [...jobByLocationPath, "locationComment"],
          params.locationComment,
        );
    }

    case `${SET_SPECIAL_INSTRUCTIONS}${ACTION_SUCCESS}`: {
      const { params } = action;

      return store
        .setIn(
          ["allJobs", params.id, "specialInstructions"],
          params.specialInstructions,
        )
        .setIn(
          [
            "jobsByLocation",
            params.locationUuid,
            JOB_FETCH_TYPE.UPCOMING,
            params.id,
            "specialInstructions",
          ],
          params.specialInstructions,
        );
    }

    case SET_CANCELLATION_REASON: {
      return store.set("cancellationReason", payload.reason);
    }

    case `${INITIALIZE_JOB_RESCHEDULING}${ACTION_SUCCESS}`: {
      return store.set("rescheduling", reschedulingFactory(payload.data));
    }

    case `${FETCH_RESCHEDULING_AVAILABILITIES}${ACTION_SUCCESS}`: {
      return store.setIn(
        ["rescheduling", "timeslots"],
        reschedulingTimeslotsFactory(payload.data),
      );
    }

    case `${INITIALIZE_AUTOMATIC_AGENT_REPLACEMENT}${ACTION_SUCCESS}`: {
      return store.set(
        "agentReplacement",
        agentReplacementFactory(payload.data),
      );
    }

    case `${FETCH_JOBS_BEYOND_PLANNING_WINDOW}${ACTION_PENDING}`: {
      return store.set(
        "loadingStateBeyondPlanningWindow",
        LOADING_STATE.LOADING,
      );
    }

    case `${FETCH_JOBS_BEYOND_PLANNING_WINDOW}${ACTION_FAIL}`: {
      return store.set(
        "loadingStateBeyondPlanningWindow",
        LOADING_STATE.FAILED,
      );
    }

    case `${FETCH_JOBS_BEYOND_PLANNING_WINDOW}${ACTION_SUCCESS}`: {
      const {
        data: { jobs, hasNextPage, cursor },
      } = payload;

      if (jobs.length === 0) {
        return store.set(
          "loadingStateBeyondPlanningWindow",
          LOADING_STATE.SUCCEEDED,
        );
      }

      const locationUuid = jobs[0].locationUuid;
      const currentJobsList = store
        .get("jobsByLocationBeyondPlanningWindow")
        .get(locationUuid);

      return store
        .setIn(
          ["jobsByLocationBeyondPlanningWindow", locationUuid],
          JobsLocationBeyondPlanningWindowFactory({
            cursor,
            hasNextPage,
            jobs: MapJobsFactory(jobs, currentJobsList?.get("jobs"), true),
          }),
        )
        .set("allJobs", MapJobsFactory(jobs, store.get("allJobs"), true))
        .set("loadingStateBeyondPlanningWindow", LOADING_STATE.SUCCEEDED);
    }

    default: {
      return store;
    }
  }
};

export { jobs2Reducer };
